import {
  CALCULATION_REFERENCE,
  CAVEATS,
  DECISIONS,
  PBI_COMPARISON,
  SOURCE_TABLES,
  type ICaveat,
  type IDecision,
  type IPbiComparisonEntry,
  type ISourceTableEntry,
} from '@skystone/shared';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { CalculationConceptCard } from './CalculationConceptCard';

/**
 * Read-only Reference tab for `/admin/quality-control`. Renders the five
 * sub-sections of the `@skystone/shared/calculation-reference` module:
 * Concepts, Decisions, Caveats, Power BI Comparison, Source Tables.
 *
 * Pure static rendering — no API calls, no state, no edit affordances. The
 * page-level `RoleBasedRoute` already gates access to DataQualityAdmin /
 * SiteAdmin.
 */
export function CalculationReferenceTab() {
  return (
    <div className="space-y-8">
      <section id="concepts" aria-labelledby="concepts-heading" className="space-y-4">
        <h2 id="concepts-heading" className="text-xl font-semibold text-foreground">
          Concepts
        </h2>
        <p className="text-sm text-muted-foreground">
          Canonical flow-metric definitions. Each concept names its source
          table, SQL filter, scope rules, consumers, and the single repository
          method that implements it.
        </p>
        <div className="space-y-4">
          {CALCULATION_REFERENCE.map((definition) => (
            <CalculationConceptCard key={definition.conceptKey} definition={definition} />
          ))}
        </div>
      </section>

      <section id="decisions" aria-labelledby="decisions-heading" className="space-y-4">
        <h2 id="decisions-heading" className="text-xl font-semibold text-foreground">
          Decisions
        </h2>
        <p className="text-sm text-muted-foreground">
          Locked decisions from the WIP canonical-definitions epic. Each
          statement is paired with the rationale that drove it.
        </p>
        <div className="space-y-3">
          {DECISIONS.map((decision) => (
            <DecisionEntry key={decision.decisionKey} decision={decision} />
          ))}
        </div>
      </section>

      <section id="caveats" aria-labelledby="caveats-heading" className="space-y-4">
        <h2 id="caveats-heading" className="text-xl font-semibold text-foreground">
          Caveats
        </h2>
        <p className="text-sm text-muted-foreground">
          Known data-quality caveats that downstream consumers should account for.
        </p>
        <div className="space-y-3">
          {CAVEATS.map((caveat) => (
            <CaveatEntry key={caveat.caveatKey} caveat={caveat} />
          ))}
        </div>
      </section>

      <section
        id="pbi-comparison"
        aria-labelledby="pbi-comparison-heading"
        className="space-y-4"
      >
        <h2 id="pbi-comparison-heading" className="text-xl font-semibold text-foreground">
          Power BI Comparison
        </h2>
        <p className="text-sm text-muted-foreground">
          Deliberate deviations from the legacy Power BI dashboard. Power BI
          parity is a secondary outcome — where Skystone differs, the reason is
          documented here.
        </p>
        <div className="space-y-3">
          {PBI_COMPARISON.map((entry) => (
            <PbiComparisonEntryCard key={entry.entryKey} entry={entry} />
          ))}
        </div>
      </section>

      <section
        id="source-tables"
        aria-labelledby="source-tables-heading"
        className="space-y-4"
      >
        <h2 id="source-tables-heading" className="text-xl font-semibold text-foreground">
          Source Tables
        </h2>
        <p className="text-sm text-muted-foreground">
          Raw / dimension tables that drive every canonical flow-metric
          definition.
        </p>
        <div className="space-y-3">
          {SOURCE_TABLES.map((table) => (
            <SourceTableEntryCard key={table.tableName} entry={table} />
          ))}
        </div>
      </section>
    </div>
  );
}

function DecisionEntry({ decision }: Readonly<{ decision: IDecision }>) {
  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-semibold text-foreground">{decision.title}</h3>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-foreground">
          <span className="font-medium">Statement:</span> {decision.statement}
        </p>
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">Rationale:</span> {decision.rationale}
        </p>
      </CardContent>
    </Card>
  );
}

function CaveatEntry({ caveat }: Readonly<{ caveat: ICaveat }>) {
  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-semibold text-foreground">{caveat.title}</h3>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-muted-foreground whitespace-pre-line">{caveat.description}</p>
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">Affects:</span>{' '}
          {caveat.affectedConcepts.join(', ')}
        </p>
      </CardContent>
    </Card>
  );
}

function PbiComparisonEntryCard({ entry }: Readonly<{ entry: IPbiComparisonEntry }>) {
  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-semibold text-foreground">{entry.metricName}</h3>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">Power BI:</span> {entry.pbiBehavior}
        </p>
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">Skystone:</span> {entry.skystoneBehavior}
        </p>
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">Reason:</span> {entry.deviationReason}
        </p>
      </CardContent>
    </Card>
  );
}

function SourceTableEntryCard({ entry }: Readonly<{ entry: ISourceTableEntry }>) {
  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-semibold text-foreground">
          <code className="rounded bg-muted px-1.5 py-0.5">{entry.tableName}</code>
        </h3>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-muted-foreground">{entry.purpose}</p>
        <div>
          <p className="font-medium text-foreground mb-1">Notable columns:</p>
          <ul className="list-disc pl-5 text-muted-foreground space-y-0.5">
            {entry.notableColumns.map((col) => (
              <li key={col}>
                <code className="rounded bg-muted px-1 py-0.5 text-xs">{col}</code>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">Feeds concepts:</span>{' '}
          {entry.feeds.join(', ')}
        </p>
      </CardContent>
    </Card>
  );
}
